// The "Set up button" dialog. The logic is in buttonwizard.js; this file draws it.
import { ButtonWizard, PRESSES_NEEDED } from './buttonwizard.js';
import { button, paragraph, openWizardDialog } from './wizard_dialog.js';

// getDevice(): the current DeviceLink. reconnect(): waits for the ESP to come back
// after its restart and reopens the serial port (resolves true when it worked).
// setPressListener(fn | null): the page hands every "pin pressed" event from the
// device to fn while the wizard is open.
export function openButtonWizard({ getDevice, reconnect, setPressListener }) {
  let wizard = null;
  let finished = false;

  const close = async () => {
    const endsTest = !finished && wizard?.testing;
    if (!finished && wizard) await wizard.cancel(); // ends a running test: the ESP restarts
    finished = true;
    setPressListener(null);
    dlg.close();
    if (endsTest) reconnect();
  };
  const dlg = openWizardDialog({ onRequestClose: close });
  const show = dlg.show;

  const begin = async (action) => {
    const device = getDevice();
    let config;
    try {
      config = (await device.getConfig())?.config;
    } catch {
      config = null;
    }
    if (!config) {
      show('That did not work', [paragraph('Could not read the device configuration.')], [button('Close', close, 'outline secondary')]);
      return;
    }
    // The LED and the bus lines are in use: not watched, so they cannot be mistaken for the button.
    const exclude = [config.led, config.led2, config.dat, config.clk, config.atn, config.rst].filter((p) => p != null);
    wizard = new ButtonWizard(device, { exclude, onChange: render });
    setPressListener((pin) => wizard?.onPress(pin));
    await action(wizard);
  };

  const intro = () => {
    show(
      'Set up the button',
      [
        paragraph('The page-break button connects one of the ESP’s pins to GND. This finds out which pin it is on: you press it a few times and the ESP reports the pin that reacted.'),
        paragraph('The BOOT button on the board works as well. The pin of the LED and the bus lines are not watched, so the button must not be on those. Do you have a button?'),
      ],
      [
        button('Cancel', close, 'outline secondary'),
        button('I have no button', () => begin((w) => saveAndReconnect(w, () => w.saveNoButton(), 'No button is stored.')), 'secondary'),
        button('Yes, find it', () => begin((w) => w.start())),
      ],
    );
  };

  const render = (w) => {
    if (w.step === 'press') {
      const seen = w.search.seen.length
        ? `Seen so far: ${w.search.seen.map(([pin, n]) => `GPIO${pin} ×${n}`).join(', ')}`
        : 'Nothing seen yet. Press the button.';
      show(
        `Press your button ${PRESSES_NEEDED} times`,
        [
          paragraph('Press and release it firmly, a bit slower than once a second. Do not touch anything else.'),
          paragraph(`<small>${seen}</small>`),
          ...(w.hint ? [paragraph(`<strong>${w.hint}</strong>`)] : []),
        ],
        [button('Cancel', close, 'outline secondary'), button('Start over', () => w.retry(), 'outline')],
      );
    } else if (w.step === 'found') {
      const boot = w.pin === 0 ? ' That is the BOOT button of the board; it is fine to use as the page-break button.' : '';
      show(
        'Button found',
        [
          paragraph(`Your button is on <strong>GPIO${w.pin}</strong>.${boot}`),
          paragraph('Saving stores this in the device and restarts it. Pressing the button then ends the page, like the Force page end button.'),
        ],
        [
          button('Cancel', close, 'outline secondary'),
          button('Wrong pin, press again', () => w.retry(), 'outline'),
          button('Save to the device', () => saveAndReconnect(w, () => w.save(), `The button on GPIO${w.pin} is set up.`)),
        ],
      );
    } else if (w.step === 'saving') {
      show('Saving…', [paragraph('Storing the setting in the device.')], []);
    } else if (w.step === 'failed') {
      show(
        'That did not work',
        [paragraph(w.error ?? 'Something went wrong.')],
        [button('Close', close, 'outline secondary'), button('Try again', () => w.retry())],
      );
    }
  };

  const saveAndReconnect = async (w, save, doneText) => {
    try {
      await save();
    } catch {
      return; // the wizard shows the failure itself
    }
    finished = true; // the restart ends the test, nothing left to cancel
    setPressListener(null);
    show('Restarting the device…', [paragraph('The device restarts to use the new setting. The page reconnects by itself.')], []);
    const ok = await reconnect();
    show(
      ok ? 'Done' : 'Saved, but the page could not reconnect',
      [paragraph(ok ? doneText : 'The setting is stored. Replug the device if needed and press Connect.')],
      [button('Close', close)],
    );
  };

  intro();
}
